import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import ministryService from '../../services/ministryService';

const MinistryCard = ({ title, description, image, path }) => {
  return (
    <div className="bg-white rounded-lg shadow-md overflow-hidden transition-transform hover:transform hover:scale-[1.02]">
      <div 
        className="h-48 bg-cover bg-center" 
        style={{ backgroundImage: `url(${image})` }}
      ></div>
      <div className="p-6">
        <h3 className="text-xl font-bold text-gray-900 mb-2">{title}</h3>
        <p className="text-gray-700 mb-4">{description}</p>
        <Link 
          to={path} 
          className="inline-block bg-indigo-600 text-white px-4 py-2 rounded font-medium hover:bg-indigo-700 transition duration-200"
        >
          Learn More
        </Link>
      </div>
    </div>
  );
};

const MinistryPage = () => {
  const [ministries, setMinistries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    // In a real implementation, this would fetch data from the API
    // For now, using predefined data until backend is connected
    const fetchMinistries = async () => {
      try {
        // This would normally be: const data = await ministryService.getAllMinistries();
        const data = [
          {
            id: 1,
            title: "Women's Ministry",
            description: "Empowering women to grow in faith, develop godly character, and serve with their unique gifts.",
            image: `${process.env.PUBLIC_URL}/images/women-ministry.jpg`,
            path: "/ministries/women"
          },
          {
            id: 2,
            title: "Youth Ministry",
            description: "Guiding and empowering the next generation to develop a personal relationship with Jesus Christ.",
            image: `${process.env.PUBLIC_URL}/images/youth-ministry.jpg`,
            path: "/ministries/youth"
          },
          {
            id: 3,
            title: "Children's Ministry",
            description: "Nurturing the faith of children through fun, biblical teaching, and building strong foundations.",
            image: `${process.env.PUBLIC_URL}/images/children-ministry.jpg`,
            path: "/ministries/children"
          },
          {
            id: 4,
            title: "Music Ministry",
            description: "Leading the congregation in worship through music that glorifies God and inspires hearts.",
            image: `${process.env.PUBLIC_URL}/images/music-ministry.jpg`,
            path: "/ministries/music"
          },
          {
            id: 5,
            title: "Outreach Ministry",
            description: "Sharing God's love with our community through acts of service and evangelism.",
            image: `${process.env.PUBLIC_URL}/images/outreach-ministry.jpg`,
            path: "/ministries/outreach"
          },
          {
            id: 6,
            title: "Prayer Ministry",
            description: "Covering the church, community, and world in prayer through organized prayer initiatives.",
            image: `${process.env.PUBLIC_URL}/images/prayer-ministry.jpg`,
            path: "/ministries/prayer"
          }
        ];
        setMinistries(data);
        setLoading(false);
      } catch (err) {
        setError("Failed to load ministries. Please try again later.");
        setLoading(false);
        console.error("Error fetching ministries:", err);
      }
    };

    fetchMinistries();
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen flex justify-center items-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-indigo-500"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen flex justify-center items-center">
        <div className="bg-red-100 text-red-700 p-4 rounded-lg max-w-md">
          <p>{error}</p>
          <button 
            onClick={() => window.location.reload()} 
            className="mt-2 bg-red-600 text-white px-4 py-2 rounded"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-12">
      <div className="container mx-auto px-4">
        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold text-gray-900 mb-4">Our Ministries</h1>
          <p className="text-xl text-gray-600 max-w-2xl mx-auto">
            Discover the various ministries of Excellent Love Ministries and find your place to serve, grow, and connect.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {ministries.map((ministry) => (
            <MinistryCard 
              key={ministry.id}
              title={ministry.title}
              description={ministry.description}
              image={ministry.image}
              path={ministry.path}
            />
          ))}
        </div>

        <div className="mt-16 bg-indigo-50 rounded-lg p-8 text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Want to Get Involved?</h2>
          <p className="text-gray-700 mb-6 max-w-2xl mx-auto">
            We believe every person has unique gifts and talents to contribute. If you'd like to serve in one of our ministries or learn more about how you can get involved, we'd love to connect with you.
          </p>
          <Link 
            to="/contact" 
            className="inline-block bg-indigo-600 text-white px-6 py-3 rounded-lg font-medium hover:bg-indigo-700 transition duration-200"
          >
            Contact Us
          </Link>
        </div>
      </div>
    </div>
  );
};

export default MinistryPage; 